/**
 * Emotions coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring_themes`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const emotionsColoringPagesPack: ToilePack = {
    slug: 'emotions-coloring-pages',
    category: 'coloring',
    name: 'Emotions coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'chart', label: 'Feelings chart', subject: 'a grid of six child faces in panels, named below "Happy", "Sad", "Angry", "Scared", "Surprised", "Calm"'},
        {key: 'happy', label: 'Happy face', subject: 'one large child\'s face laughing with closed eyes, curly hair and a gap-toothed grin, sun rays behind and the word "Happy" below'},
        {key: 'today', label: 'How I feel today', subject: 'a large blank face outline with no features for the child to draw, the words "Today I feel" above and an empty line below'},
    ],
    styles: [
        {key: 'easy', label: 'Easy', style: 'an easy style for young children, thick bold uniform outlines, big round faces and very clear expressions', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'kawaii', label: 'Kawaii', style: 'a cute kawaii cartoon style, smooth even outlines, round cheeks, big eyes and small hearts and stars around', palette: 'black ink only', framing: 'centered with a white margin'},
        {key: 'doodle', label: 'Doodle', style: 'a hand-drawn marker doodle style, slightly wobbly friendly lines and a doodled border of squiggles around the page', palette: 'black ink only', framing: 'the full page used inside the doodled border'},
    ],
    directions: ['worried', 'a classroom', 'proud'],
    directionHint: 'try: worried',
    seo: {
        title: 'Emotions coloring pages: feelings chart and faces',
        metaDescription: "Nine emotions coloring pages: a chart of six feelings, a child's happy face and a blank face to draw today's feeling.",
        h1: 'Emotions coloring pages: feelings chart, happy face, how I feel today',
        tagline: "A chart of six feelings, one clear mood and a blank face to draw today's feeling.",
        intro: "Emotions coloring pages used by parents, teachers and counsellors to help children name what they feel. The nine pages show a chart of six faces with their feelings written under them, a child's face laughing in one clear mood, and a blank face to draw today's feeling. The styles are easy, kawaii and doodle. Type a different feeling, and the same style carries it.",
        useCases: [
            'a feelings chart on a classroom wall',
            'a check-in page for a child at home',
            'a session page for a school counsellor',
            'a daily page to draw how the day felt',
        ],
        faq: [
            {q: 'Which six feelings are on the chart?', a: 'Happy, sad, angry, scared, surprised and calm, each written under its face.'},
            {q: 'What is on the blank face page?', a: 'A large face outline with no features, and the words Today I feel above an empty line, for the child to draw the feeling.'},
            {q: 'What is the difference between the styles?', a: 'Easy uses thick outlines and very clear expressions. Kawaii adds round cheeks and small hearts and stars, and doodle is a hand-drawn marker line with a squiggle border.'},
            {q: 'Are the pages in color?', a: 'No. Each page is a black line drawing on white paper, ready to print and color.'},
        ],
    },
}
